// drawSetup.ts — Patch 010.C.2.1 (Deep Draws, sub-patch 1 of 3).
//
// Draw counterparties setup: per-project lender + title-company records and
// their draw-request template uploads. Backend: project_lenders +
// project_title_companies + draw_templates (migration 0065) +
// workers/core-os/src/routes/draw_setup.ts.

import { ApiError, BASE, request } from './request'

export type DrawPartyKind = 'lender' | 'title'

export interface DrawParty {
  id: string
  project_id: string
  org_name: string
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  notes: string | null
  archived_at: string | null
  created_at: string
  updated_at: string | null
}

export interface DrawTemplate {
  id: string
  project_id: string
  party_kind: DrawPartyKind
  project_lender_id: string | null
  project_title_company_id: string | null
  document_id: string
  // Patch 016.1.R — dual-document support. .doc/.docx/.xlsx is at
  // document_id (fill source); PDF for AI parsing is at parse_pdf_document_id.
  parse_pdf_document_id: string | null
  parse_status: 'pending' | 'parsed' | 'failed' | 'superseded'
  parsed_field_map_json: string | null
  parsed_at: string | null
  created_at: string
  updated_at: string | null
  document_filename: string | null
  document_mime_type: string | null
  parse_pdf_filename: string | null
  parse_pdf_mime_type: string | null
}

export interface DrawPartyInput {
  org_name: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  notes?: string
}

export interface PatchDrawPartyInput {
  org_name?: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  notes?: string
  archived?: boolean
}

// kind -> URL path segment.
function seg(kind: DrawPartyKind): string {
  return kind === 'lender' ? 'lenders' : 'title-companies'
}
const pid = (id: string): string => encodeURIComponent(id)

export const drawSetupApi = {
  listParties: (projectId: string, kind: DrawPartyKind) =>
    request<{ ok: true; parties: DrawParty[] }>(
      `/api/projects/${pid(projectId)}/${seg(kind)}`,
    ),
  createParty: (projectId: string, kind: DrawPartyKind, body: DrawPartyInput) =>
    request<{ ok: true; id: string; request_id: string }>(
      `/api/projects/${pid(projectId)}/${seg(kind)}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateParty: (
    projectId: string,
    kind: DrawPartyKind,
    partyId: string,
    body: PatchDrawPartyInput,
  ) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${pid(projectId)}/${seg(kind)}/${pid(partyId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  uploadTemplate: async (
    projectId: string,
    kind: DrawPartyKind,
    partyId: string,
    file: File,
  ) => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch(
      `${BASE}/api/projects/${pid(projectId)}/${seg(kind)}/${pid(partyId)}/template`,
      { method: 'POST', credentials: 'include', body: form },
    )
    const body = (await res.json().catch(() => null)) as unknown
    if (!res.ok) {
      const err = (body as { error?: string })?.error ?? `http_${res.status}`
      throw new ApiError(err, res.status, body)
    }
    return body as {
      ok: true
      template_id: string
      document_id: string
      // Patch 016.1.R — which slot the upload filled. 'fill' = .doc/.docx
      // for Render filling. 'parse_pdf' = PDF for Anthropic AI parsing.
      role: 'parse_pdf' | 'fill'
      request_id: string
    }
  },
  listTemplates: (projectId: string) =>
    request<{ ok: true; templates: DrawTemplate[] }>(
      `/api/projects/${pid(projectId)}/draw-templates`,
    ),
  // Patch 010.C.2.2 — AI-parse an uploaded template into a field map.
  parseTemplate: (projectId: string, templateId: string) =>
    request<{
      ok: boolean
      template_id?: string
      parse_status?: string
      field_count?: number
      error?: string
      detail?: string
    }>(
      `/api/projects/${pid(projectId)}/draw-templates/${pid(templateId)}/parse`,
      { method: 'POST', body: '{}' },
    ),
}
